import { useState, useMemo } from 'react';

interface Props {
  json: string;
  pathPrefix: string;
  onCopyTemplate?: (template: string) => void;
}

function safeParse(json: string): unknown {
  try { return JSON.parse(json); } catch { return json; }
}

function isObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function joinPath(prefix: string, key: string | number): string {
  if (typeof key === 'number') return `${prefix}[${key}]`;
  if (!prefix) return key;
  return `${prefix}.${key}`;
}

function formatLeaf(v: unknown): string {
  if (v === null) return 'null';
  if (typeof v === 'string') return `"${v}"`;
  return String(v);
}

function leafColor(v: unknown): string {
  if (v === null) return 'text-muted-foreground';
  if (typeof v === 'string') return 'text-success';
  if (typeof v === 'number') return 'text-tone-amber';
  if (typeof v === 'boolean') return 'text-tone-violet';
  return 'text-foreground';
}

function CopyButton({ template, onCopyTemplate, label = 'copy' }: {
  template: string; onCopyTemplate?: (t: string) => void; label?: string;
}) {
  const [done, setDone] = useState(false);
  function copy() {
    if (onCopyTemplate) onCopyTemplate(template);
    void navigator.clipboard?.writeText(template);
    setDone(true);
    setTimeout(() => setDone(false), 900);
  }
  return (
    <button
      type="button"
      onClick={copy}
      title={`Copy template ${template}`}
      className="opacity-0 group-hover:opacity-100 transition-opacity text-[10px] px-1.5 py-0.5 rounded bg-primary text-primary-foreground hover:bg-primary/90 font-mono"
    >
      {done ? '✓' : label}
    </button>
  );
}

interface NodeProps {
  k: string | number | null;
  v: unknown;
  path: string;
  depth: number;
  onCopyTemplate?: (t: string) => void;
}

function TreeNode({ k, v, path, depth, onCopyTemplate }: NodeProps) {
  const [open, setOpen] = useState(depth < 2);
  const template = `{{${path}}}`;
  const indent = { paddingLeft: depth * 12 };

  if (Array.isArray(v)) {
    return (
      <div style={indent} className="group flex flex-col">
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setOpen(o => !o)}
            className="text-muted-foreground hover:text-foreground w-3 text-xs"
            type="button"
          >
            {open ? '▾' : '▸'}
          </button>
          {k !== null && (
            <span className="font-mono text-xs text-tone-blue">
              {typeof k === 'number' ? `[${k}]` : k}:
            </span>
          )}
          <span className="text-xs text-muted-foreground">
            Array({v.length})
          </span>
          <CopyButton template={template} onCopyTemplate={onCopyTemplate} />
          {v.length > 0 && (
            <>
              <CopyButton template={`{{${path} | first}}`} onCopyTemplate={onCopyTemplate} label="| first" />
              <CopyButton template={`{{${path} | count}}`} onCopyTemplate={onCopyTemplate} label="| count" />
            </>
          )}
        </div>
        {open && v.map((item, i) => (
          <TreeNode
            key={i}
            k={i}
            v={item}
            path={joinPath(path, i)}
            depth={depth + 1}
            onCopyTemplate={onCopyTemplate}
          />
        ))}
      </div>
    );
  }

  if (isObject(v)) {
    const keys = Object.keys(v);
    return (
      <div style={indent} className="group flex flex-col">
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setOpen(o => !o)}
            className="text-muted-foreground hover:text-foreground w-3 text-xs"
            type="button"
          >
            {open ? '▾' : '▸'}
          </button>
          {k !== null && (
            <span className="font-mono text-xs text-tone-blue">
              {typeof k === 'number' ? `[${k}]` : k}:
            </span>
          )}
          <span className="text-xs text-muted-foreground">
            {`{${keys.length}}`}
          </span>
          {path && <CopyButton template={template} onCopyTemplate={onCopyTemplate} />}
        </div>
        {open && keys.map(key => (
          <TreeNode
            key={key}
            k={key}
            v={v[key]}
            path={joinPath(path, key)}
            depth={depth + 1}
            onCopyTemplate={onCopyTemplate}
          />
        ))}
      </div>
    );
  }

  return (
    <div style={indent} className="group flex items-center gap-1.5 min-h-[18px]">
      <span className="w-3" />
      {k !== null && (
        <span className="font-mono text-xs text-tone-blue">
          {typeof k === 'number' ? `[${k}]` : k}:
        </span>
      )}
      <span className={`font-mono text-xs ${leafColor(v)} break-all`}>
        {formatLeaf(v)}
      </span>
      <CopyButton template={template} onCopyTemplate={onCopyTemplate} />
    </div>
  );
}

export function JsonPathTree({ json, pathPrefix, onCopyTemplate }: Props) {
  const parsed = useMemo(() => safeParse(json), [json]);
  if (typeof parsed === 'string') {
    return (
      <pre className="p-2 bg-muted/60 rounded-md text-xs overflow-x-auto font-mono">
        {parsed}
      </pre>
    );
  }
  return (
    <div className="p-2 bg-muted/60 rounded-md font-mono overflow-x-auto">
      <TreeNode k={null} v={parsed} path={pathPrefix} depth={0} onCopyTemplate={onCopyTemplate} />
    </div>
  );
}
